import React, { useState, useEffect, forwardRef, useImperativeHandle, useRef, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import ChatWithAI from "./ChatWithAI";
import { Expert, DEFAULT_EXPERT } from "@/models/Expert";
import { expertService } from "@/services/expertService";
import { Menu, Star, Share2, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CreditDisplay } from "@/components/ui/credit-display";
import ExpertsList from "./ExpertsList";
import { useNavigate, useLocation } from "react-router-dom";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

interface ChatLayoutProps {
  isWidget?: boolean;
  fullHeight?: boolean;
  hideHeader?: boolean;
  contextData?: any;
  title?: string;
  onMessageSent?: () => void;
  initialExpertId?: string;
  className?: string;
  onExpertChange?: (expert: Expert) => void;
  autoScrollDisabled?: boolean;
  isMobileLayout?: boolean;
}

interface ChatLayoutRef {
  setExpert: (expertId: string) => void;
  getSelectedExpert: () => Expert | null;
  clearChat: () => void;
  toggleExpertsList: () => void;
}

/**
 * Layout chính cho tính năng chat với AI
 */
const ChatLayout = forwardRef<ChatLayoutRef, ChatLayoutProps>(({
  isWidget = false,
  fullHeight = false,
  hideHeader = false,
  contextData = {},
  title = "",
  onMessageSent,
  initialExpertId,
  className = "",
  onExpertChange,
  autoScrollDisabled = false,
  isMobileLayout = false
}, ref) => {
  const { t } = useTranslation();
  const [selectedExpertId, setSelectedExpertId] = useState<string>(initialExpertId || DEFAULT_EXPERT.id);
  const [selectedExpert, setSelectedExpert] = useState<Expert | null>(null);
  const [isExpertsListVisible, setIsExpertsListVisible] = useState<boolean>(false);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const layoutRef = useRef<HTMLDivElement>(null);
  const chatRef = useRef<any>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const fetchInProgressRef = useRef<{[key: string]: boolean}>({});
  
  // Memoize default expert context
  const defaultExpertContext = useMemo(() => ({
    expertId: DEFAULT_EXPERT.id,
    expertName: DEFAULT_EXPERT.name,
    expertAvatar: DEFAULT_EXPERT.avatar,
    expertGreeting: DEFAULT_EXPERT.greeting,
    expert: DEFAULT_EXPERT,
  }), []);
  
  // Thông tin bổ sung từ context (từ props contextData)
  const contextInfo = useMemo(() => contextData || {}, [contextData]);

  // Kiểm tra màn hình mobile
  useEffect(() => {
    const checkIfMobile = () => {
      const isMobileView = window.innerWidth < 768;
      setIsMobile(isMobileView || isMobileLayout);
      
      // Nếu đang ở desktop, luôn hiển thị expert list
      if (!isMobileView && !isMobileLayout && !isExpertsListVisible) {
        setIsExpertsListVisible(true);
      } else if ((isMobileView || isMobileLayout) && isExpertsListVisible) {
        // Trên mobile, mặc định ẩn
        setIsExpertsListVisible(false);
      }
    };
    
    checkIfMobile();
    window.addEventListener("resize", checkIfMobile);
    
    return () => {
      window.removeEventListener("resize", checkIfMobile);
    };
  }, [isExpertsListVisible, isMobileLayout]);

  // Load expert details
  const fetchExpert = useCallback(async (id: string): Promise<Expert | null> => {
    try {
      // Nếu ID không hợp lệ hoặc đang loading, không thực hiện fetch
      if (!id || id === "loading") {
        return selectedExpert; 
      }
      
      // Kiểm tra xem chúng ta đã có expert với ID này chưa
      if (selectedExpert && selectedExpert.id === id) {
        return selectedExpert;
      }
      
      // Kiểm tra nếu đang fetch expert này rồi thì không fetch lại
      if (fetchInProgressRef.current[id]) {
        return null;
      }
      
      // Đánh dấu đang fetch expert này
      fetchInProgressRef.current[id] = true;
      setIsLoading(true);
      
      const expert = await expertService.getExpertById(id);
      
      // Xóa đánh dấu fetch
      fetchInProgressRef.current[id] = false;
      setIsLoading(false);
      
      if (expert) {
        setSelectedExpert(expert);
        
        // Notify parent about expert change
        if (onExpertChange) {
          onExpertChange(expert);
        }
        
        return expert;
      }
      return null;
    } catch (error) {
      // Xóa đánh dấu fetch nếu có lỗi
      fetchInProgressRef.current[id] = false;
      setIsLoading(false);
      console.error("Error fetching expert:", error);
      return null;
    }
  }, [onExpertChange, selectedExpert]);

  // Toggle danh sách experts
  const toggleExpertsList = useCallback(() => {
    setIsExpertsListVisible(prev => !prev);
  }, []);

  // Xử lý khi chọn expert
  const handleSelectExpert = useCallback((expertId: string) => {
    // Không làm gì nếu đang chọn expert hiện tại
    if (expertId === selectedExpertId || isLoading) {
      // Trên mobile, vẫn cần ẩn danh sách
      if (isMobile) {
        setIsExpertsListVisible(false);
      }
      return;
    }
    
    // Cập nhật state
    setSelectedExpertId(expertId);
    
    // Cập nhật URL nếu không phải là widget
    if (!isWidget) {
      // Kiểm tra xem đường dẫn hiện tại có phải là /chat hoặc /chat/expert/... hay không
      if (location.pathname.startsWith('/chat')) {
        // Cập nhật URL để phản ánh sự thay đổi expert
        navigate(`/chat/expert/${expertId}`, { replace: true });
      }
    }
    
    // Lấy thông tin expert để chuẩn bị cho việc cập nhật conversation
    fetchExpert(expertId).then((expert) => {
      if (expert) {
        // Tăng timeout để đảm bảo thông tin expert đã được cập nhật đầy đủ
        setTimeout(() => {
          if (chatRef.current && chatRef.current.resetWelcomeMessage) {
            chatRef.current.resetWelcomeMessage();
          }
        }, 300);
      }
    });
    
    // Trên mobile, ẩn danh sách sau khi chọn
    if (isMobile) {
      setIsExpertsListVisible(false);
    }
  }, [isMobile, selectedExpertId, isWidget, navigate, location.pathname, fetchExpert, isLoading]);

  // Expose methods to parent
  useImperativeHandle(ref, () => ({
    setExpert: (expertId: string) => {
      handleSelectExpert(expertId);
    },
    getSelectedExpert: () => selectedExpert,
    clearChat: () => {
      if (chatRef.current && chatRef.current.clearConversation) {
        chatRef.current.clearConversation();
      }
    },
    toggleExpertsList: () => {
      toggleExpertsList();
    }
  }), [selectedExpert, handleSelectExpert, toggleExpertsList]);

  // Gọi API lấy thông tin expert khi ID thay đổi
  useEffect(() => {
    // Nếu đang loading hoặc không có ID, không thực hiện fetch
    if (isLoading || !selectedExpertId || selectedExpertId === "loading") {
      return;
    }
    
    // Chỉ fetch khi selectedExpert chưa được set hoặc ID khác với selectedExpertId
    const shouldFetch = !selectedExpert || selectedExpert.id !== selectedExpertId;
    
    if (shouldFetch) {
      // Đánh dấu đang loading
      setSelectedExpertId(prevId => prevId === selectedExpertId ? "loading" : prevId);
      
      fetchExpert(selectedExpertId)
        .then(expert => {
          // Khôi phục ID sau khi fetch xong
          if (selectedExpertId === "loading") {
            setSelectedExpertId(expert?.id || "default");
          }
        })
        .catch(() => {
          // Khôi phục ID nếu có lỗi
          if (selectedExpertId === "loading") {
            setSelectedExpertId("default");
          }
        });
    }
  }, [selectedExpertId, fetchExpert, selectedExpert, isLoading]);

  // Handle clear chat - đảm bảo chat được xóa đúng cách
  const handleClearChat = useCallback(() => {
    // Đảm bảo xóa hoàn toàn cuộc hội thoại và tạo một tin nhắn welcome mới
    if (chatRef.current && chatRef.current.clearConversation) {
      // Xóa trước
      chatRef.current.clearConversation();
      
      // Sau đó tạo lại welcome message với thông tin expert hiện tại
      setTimeout(() => {
        if (chatRef.current && chatRef.current.resetWelcomeMessage) {
          chatRef.current.resetWelcomeMessage();
        }
      }, 100);
    }
  }, []);

  // Prepare the context data for ChatWithAI
  const prepareContextData = useMemo(() => {
    if (!selectedExpert || !selectedExpert.id) {
      return defaultExpertContext;
    }

    // Create the context data with extremely clear properties
    return {
      expertId: selectedExpert.id,
      expertName: selectedExpert.name,
      expertAvatar: selectedExpert.avatar,
      expertGreeting: selectedExpert.greeting,
      expert: selectedExpert, // Truyền toàn bộ object expert
      ...contextInfo, // Spread existing context info
    };
  }, [selectedExpert, contextInfo, defaultExpertContext]);

  return (
    <div className="h-full flex flex-col bg-gray-50 dark:bg-gray-900 py-1">
      <div className="container mx-auto max-w-6xl px-4 md:px-6 flex flex-1 overflow-hidden">
        {/* Main layout */}
        <div className={cn(
          "flex flex-1 rounded-lg shadow-sm overflow-hidden bg-white dark:bg-gray-800",
          isMobile ? "px-0" : ""
        )}>
          {/* Left sidebar - Fixed width */}
          <div className={cn(
            "w-64 bg-white dark:bg-gray-800 border-r dark:border-gray-700 flex-shrink-0",
            isMobile ? "hidden" : "flex flex-col"
          )}>
            {/* Sidebar header with logo and search */}
            <div className="border-b dark:border-gray-700 p-3 flex items-center">
              <div className="flex flex-col">
                <div className="text-lg font-medium">{t("buddha_chat", { ns: "ai" })}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">{t("chat_with_masters", { ns: "ai" })}</div>
              </div>
            </div>

            {/* Experts list in sidebar */}
            <div className="flex-1 overflow-hidden flex flex-col">
              <ExpertsList
                selectedExpertId={selectedExpertId}
                onSelectExpert={handleSelectExpert}
                isMobile={false}
              />
            </div>
          </div>

          {/* Chat area - Flex grow */}
          <div className="flex-1 flex flex-col overflow-hidden bg-muted/5">
            {/* Chat header - only on desktop */}
            {!hideHeader && !isMobile && (
              <div className="border-b dark:border-gray-700 p-3 flex justify-between items-center bg-white dark:bg-gray-800">
                <div className="flex items-center gap-3">
                  {selectedExpert && (
                    <div className="flex items-center gap-2">
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={selectedExpert.avatar} alt={selectedExpert.name} />
                        <AvatarFallback>{selectedExpert.name.charAt(0)}</AvatarFallback>
                      </Avatar>
                      <div className="flex flex-col">
                        <span className="font-medium text-sm">{selectedExpert.name}</span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">{selectedExpert.title}</span>
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <Star className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <Share2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}

            {/* Mobile experts list popup */}
            {isMobile && isExpertsListVisible && (
              <div className="fixed inset-0 z-50 bg-black bg-opacity-50">
                <div className="absolute top-0 left-0 h-full w-4/5 max-w-xs bg-white dark:bg-gray-800 shadow-xl flex flex-col">
                  {/* Header */}
                  <div className="border-b dark:border-gray-700 p-4 flex justify-between items-center">
                    <div className="text-lg font-medium">{t("select_expert", { ns: "ai" })}</div>
                    <Button variant="ghost" size="icon" onClick={toggleExpertsList} className="h-8 w-8">
                      <Menu className="h-4 w-4" />
                    </Button>
                  </div>
                  
                  {/* Experts list */}
                  <div className="flex-1 overflow-auto">
                    <ExpertsList
                      selectedExpertId={selectedExpertId}
                      onSelectExpert={handleSelectExpert}
                      isMobile={true}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Chat content with chat actions above input */}
            <div className="flex-1 overflow-hidden flex flex-col">
              {/* Chat messages area */}
              <div className="flex-1 overflow-auto">
                <ChatWithAI
                  ref={chatRef}
                  contextData={prepareContextData}
                  isWidget={isWidget}
                  hideHeader={true} // Always hide header as we have our own
                  fullHeight={true}
                  onMessageSent={onMessageSent}
                  autoScrollDisabled={autoScrollDisabled}
                  renderActionsBeforeInput={() => (
                    <div className={cn(
                      "flex justify-between items-center border-t dark:border-gray-700 actions-before-input",
                      isMobile ? "px-2 py-2" : "px-4 py-3"
                    )}>
                      <div className="flex items-center">
                        {isMobile ? (
                          <button
                            type="button"
                            className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 underline"
                            onClick={handleClearChat}
                          >
                            {t("clear_history", { ns: "ai" })}
                          </button>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleClearChat}
                            className="clear-history-button"
                          >
                            <Trash2 className="h-4 w-4 mr-1.5 clear-history-icon" />
                            {t("clear_history", { ns: "ai" })}
                          </Button>
                        )}
                      </div>
                      <div className="justify-end">
                        {contextInfo && contextInfo.availableCredits !== undefined && (
                          <CreditDisplay 
                            credits={contextInfo.availableCredits} 
                            compact 
                            className={isMobile ? "text-xs text-gray-500" : ""}
                          />
                        )}
                      </div>
                    </div>
                  )}
                  inputClassName="w-full h-[60px] px-4 py-3 resize-none focus:outline-none"
                  isMobileLayout={isMobile} // Truyền prop isMobileLayout xuống cho ChatWithAI
                  hideTopHeader={true} // Thêm prop này để ẩn header trong MobileChatUI, tránh bị hiển thị 2 header
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

ChatLayout.displayName = "ChatLayout";

export default ChatLayout; 